import React from 'react';

import { getCurrentStudyProgram } from '../../utils/getCurrentStudyProgram';
import {
  ANSWERS_LIMIT_REACHED,
  NO_MORE_QUESTIONS_TEXT,
} from '../../utils/questionConstants';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../utils/sessionStorage';
import CircularLoading from '../Common/CircularLoading/CircularLoading';
import QuestionMain from './QuestionMain.jsx';

class QuestionContainer extends React.Component {
  constructor(props) {
    super(props);

    // Check if we're in preview mode - handle hash-based routing
    const hash = window.location.hash;
    const isPreviewMode = hash.includes('questionPreview');
    let previewData = null;

    if (isPreviewMode) {
      // Extract data parameter from hash URL
      const hashParts = hash.split('?');
      if (hashParts.length > 1) {
        const params = new URLSearchParams(hashParts[1]);
        previewData = params.get('data');
      }
    }

    // Parse preview question data if available
    // Note: URLSearchParams.get() already decodes percent-encoding, so
    // previewData is the raw JSON string here (no extra decodeURIComponent).
    let previewQuestion = null;
    if (isPreviewMode && previewData) {
      try {
        previewQuestion = JSON.parse(previewData);
      } catch {}
    }

    this.state = {
      clicked: isPreviewMode, // In preview mode, start with clicked=true to show answers
      questionsData: isPreviewMode && previewQuestion ? [previewQuestion] : [],
      questionsUpdated: false,
      isCorrect: false,
      counters: {},
      questionIndex: 0,
      reloadPage: false,
      previousAnswerClicked: {},
      showPreviousAnswer: false,
      intervalSeted: false,
      timerStarted: null,
      redirectToDashboard: false,
      questionInReviewAsked: false,
      isMounted: false,
      loadingQuestion: false,
      isPreviewMode,
      previewQuestion,
      pendingAnswer: null,
      showTrialModal: false,
    };
  }

  componentWillUnmount() {
    clearInterval(this.state.timerStarted);
    this.props.openPdfViewer(false);
    this.setState({
      reloadPage: false,
      isMounted: false,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.cantStudy && !prevProps.cantStudy) {
      this.setState({ showTrialModal: true });
      return;
    }

    // Trial limit
    if (
      this.props.answerResponse === ANSWERS_LIMIT_REACHED &&
      prevProps.answerResponse !== ANSWERS_LIMIT_REACHED &&
      this.state.pendingAnswer
    ) {
      this.props.getTrialUserProgress(
        this.state.pendingAnswer.chapterlId,
        this.state.pendingAnswer.studyMethod,
      );
      this.setState({ pendingAnswer: null, showTrialModal: true });
      this.props.clearAnswerResponse();
      return;
    }

    if (
      this.props.answerResponse &&
      this.props.answerResponse !== prevProps.answerResponse &&
      this.props.answerResponse !== ANSWERS_LIMIT_REACHED &&
      this.state.pendingAnswer
    ) {
      const answ = this.state.pendingAnswer;
      this.setState({ pendingAnswer: null }, () => {
        this.handleNextQuestion(answ);
      });
      this.props.clearAnswerResponse();
      return;
    }

    const finishedLoadingQuestions =
      prevProps.isLoadingQuestions && !this.props.isLoadingQuestions;
    // Open reference or chapter pdf when link is received from API
    if (prevProps.pdfLink !== this.props.pdfLink && !this.props.showPdfViewer) {
      window.open(this.props.pdfLink.url);
    }

    if (prevProps.isLoadingQuestions !== this.props.isLoadingQuestions) {
      this.setState({
        loadingQuestion: !this.state.loadingQuestion,
      });
    }

    if (prevProps.loadResetChapter !== this.props.loadResetChapter) {
      // Favorite
      if (this.props.isStudyingFavoriteQuestions) {
        this.props.getQuestionsByFavoriteQuestions([], true);
      }
      if (this.props.isStudyingMyFavoriteQuestions) {
        this.props.getQuestionsByMyFavoriteQuestions([], true);
      }

      // Submit
      if (this.props.isStudyingSubmittedQuestions) {
        this.props.getQuestionsBySubmittedQuestions([], true);
      }
      if (this.props.isStudyingMySubmittedQuestions) {
        this.props.getQuestionByMySubmittedQuestions([], true);
      }
    }

    const favoriteQuestion = !!this.props.location.pathname.includes(
      'studyFavoriteQuestion',
    );

    const myFavoriteQuestion = this.props.location.pathname.includes(
      'studyMyFavoriteQuestion',
    );
    const mySubmittedQuestions = this.props.location.pathname.includes(
      'studyMySubmittedQuestion',
    );
    const submittedQuestions = this.props.location.pathname.includes(
      'studySubmittedQuestions',
    );

    if (prevProps.isStudyingFavoriteQuestions !== favoriteQuestion) {
      if (favoriteQuestion) {
        this.props.setIsStudyFavoriteQuestion(true);
      } else {
        this.props.setIsStudyFavoriteQuestion(false);
      }
    }

    if (prevProps.isStudyingSubmittedQuestions !== submittedQuestions) {
      if (submittedQuestions) {
        this.props.setIsStudyingSubmittedQuestions(true);
      } else {
        this.props.setIsStudyingSubmittedQuestions(false);
      }
    }

    if (prevProps.isStudyingMyFavoriteQuestions !== myFavoriteQuestion) {
      if (myFavoriteQuestion) {
        this.props.setIsStudyingMyFavoriteQuestions(true);
      } else {
        this.props.setIsStudyingMyFavoriteQuestions(false);
      }
    }

    if (prevProps.isStudyingMySubmittedQuestions !== mySubmittedQuestions) {
      if (mySubmittedQuestions) {
        this.props.setIsStudyingMySubmittedQuestions(true);
      } else {
        this.props.setIsStudyingMySubmittedQuestions(false);
      }
    }

    // THIS WAS IN UNSAFE_componentWillReceiveProps
    if (
      performance.navigation.type === 1 ||
      this.props.questionsData.length === 0
    ) {
      if (
        this.props.user.rate !== undefined &&
        this.state.reloadPage === false &&
        getCurrentStudyProgram(this.props.user, this.props.pfeSktId) !==
          undefined
      ) {
        this.reloadPageGetQuestions(this.props);
        this.setState({ reloadPage: true });
      }
    }

    if (
      !this.props.isLoadingQuestions &&
      finishedLoadingQuestions &&
      this.props.question === NO_MORE_QUESTIONS_TEXT
    ) {
      const noMoreQuestions = this.props.questionsData.find(
        (s) => s === NO_MORE_QUESTIONS_TEXT,
      );

      if (!noMoreQuestions) {
        const stacked = this.props.questionsData.map((s) => s.id);

        if (this.props.question?.id) {
          stacked.push(this.props.question.id);
        }

        if (prevProps.question?.id) {
          stacked.push(prevProps.question.id);
        }
      }
      this.props.history('/app/flashcard');
    }
    if (this.props.question === prevProps.question || !prevProps.question)
      return;
    if (
      this.props.questionsData.length === 1 &&
      !this.props.isLoadingQuestions
    ) {
      const noMoreQuestions = this.props.questionsData.find(
        (s) => s === NO_MORE_QUESTIONS_TEXT,
      );
      if (!noMoreQuestions) {
        const stacked = this.props.questionsData.map((s) => s.id);

        if (this.props.question && this.props.question.id) {
          stacked.push(this.props.question.id);
        }

        if (prevProps.question && prevProps.question.id) {
          stacked.push(prevProps.question.id);
        }

        const hashLocationURL = window.location.hash;
        let hashLocation = hashLocationURL.split('/').splice(3, 2);
        let id = null;
        let studyProgramId = null;

        if (hashLocation && hashLocation !== '' && hashLocation.length > 0) {
          if (
            hashLocation[0] === 'studyFavoriteQuestion' ||
            hashLocation[0] === 'studySubmittedQuestions' ||
            hashLocation[0] === 'studyMyFavoriteQuestion' ||
            hashLocation[0] === 'studyMySubmittedQuestion'
          ) {
            hashLocation = hashLocation[0];
          } else {
            if (hashLocation.length > 1 && hashLocation[1]) {
              id = hashLocation[1].split('?')[0];
              studyProgramId = hashLocation.length > 0 ? hashLocation[0] : null;
            } else {
              // Fallback
              return;
            }
          }
        }

        switch (true) {
          case hashLocation === 'studySubmittedQuestions':
            this.props.getQuestionsBySubmittedQuestions(
              stacked,
              !this.props.question.id,
            );
            break;

          case hashLocation === 'studyFavoriteQuestion':
            this.props.getQuestionsByFavoriteQuestions(
              stacked,
              !this.props.question.id,
            );
            break;

          case hashLocation === 'studyMyFavoriteQuestion':
            this.props.getQuestionsByMyFavoriteQuestions(
              stacked,
              !this.props.question.id,
            );
            break;

          case hashLocation === 'studyMySubmittedQuestion':
            this.props.getQuestionByMySubmittedQuestions(
              stacked,
              !this.props.question.id,
            );
            break;

          case hashLocationURL.includes('?isChapterId'):
            this.props.getQuestionsByChapter(
              id,
              studyProgramId,
              stacked,
              !this.props.question.id,
            );
            break;

          case hashLocationURL.includes('?isStudyPlanChapter'):
            this.props.getQuestionsByChapter(
              id,
              studyProgramId,
              stacked,
              !this.props.question.id,
            );
            break;

          // case hashLocationURL.includes('?isQualId'):
          //   this.props.getQuestionsByQuals(
          //     id,
          //     studyProgramId,
          //     stacked,
          //     !this.props.question.id,
          //   );
          //   break;

          case hashLocationURL.includes('?isPersonalizedStudyAll'):
            this.props.getQuestionsByPersonalizedStudyAll(
              this.props.user.studyPrograms[0],
              stacked,
              !this.props.question.id,
            );
            break;

          default:
            this.props.getQuestionsByRate(stacked, !this.props.question.id);
            break;
        }
      }
    }
    if (
      this.props.user &&
      !this.state.questionInReviewAsked &&
      this.props?.question?.chapter
    ) {
      const hashLocationURL = window.location.hash;
      if (hashLocationURL.includes('studySubmittedQuestions')) {
        this.props.checkChapterInReview({
          method: 'submit',
        });
      } else if (hashLocationURL.includes('studyFavoriteQuestion')) {
        this.props.checkChapterInReview({
          method: 'favorite',
        });
      } else if (hashLocationURL.includes('studyMyFavoriteQuestion')) {
        this.props.checkChapterInReview({
          method: 'myFavorite',
        });
      } else if (hashLocationURL.includes('studyMySubmittedQuestion')) {
        this.props.checkChapterInReview({
          method: 'mySubmit',
        });
      } else {
        const x = this.props.question
          ? { chapter: this.props.question.chapter }
          : null;
        this.props.checkChapterInReview(x);
      }

      this.setState({ questionInReviewAsked: true });
    }
    // if (this.props.backTo) {
    //   if (this.props.backTo === '/app/study/quals-and-certs') {
    //     this.props.breadcrumsQualsAndCertsAction('QUALS AND CERTS');
    //   }
    // }
  }

  updateIsCorrect = (Correct) => {
    this.setState({
      isCorrect: Correct,
    });
  };

  handleSendAnswer = (difficulty, thumb = null) => {
    const question = this.props.question;
    const answ = {};
    answ.correctAnswer = question.correctAnswer;
    answ.counterType = question.counterType;
    answ.difficulty = difficulty;
    answ.isCorrect = this.state.isCorrect;
    answ.isFlashcard = question.flashMode;
    answ.questionId = question.id;
    answ.questionThumb = thumb;
    answ.studyProgramId = getCurrentStudyProgram(
      this.props.user,
      this.props.pfeSktId,
    ).id;
    answ.dontEarnPoints = this.state.showPreviousAnswer;

    const hashLocationURL = window.location.hash;
    let hashLocation = hashLocationURL.split('/').splice(3, 2);
    let id = null;
    let studyProgramId = null;

    if (hashLocation && hashLocation !== '' && hashLocation.length > 0) {
      if (
        hashLocation[0] === 'studyFavoriteQuestion' ||
        hashLocation[0] === 'studySubmittedQuestions' ||
        hashLocation[0] === 'studyMyFavoriteQuestion' ||
        hashLocation[0] === 'studyMySubmittedQuestion'
      ) {
        hashLocation = hashLocation[0];
      } else {
        if (hashLocation.length > 1 && hashLocation[1]) {
          id = hashLocation[1].split('?')[0];
          studyProgramId = hashLocation.length > 0 ? hashLocation[0] : null;
          answ.studyProgramId = studyProgramId;
        } else {
          id = null;
          studyProgramId = hashLocation[0];
        }
      }
    }

    switch (true) {
      case hashLocationURL.includes('?isChapterId'):
        answ.studyMethod = 'BY_REFERENCE';
        answ.chapterlId = id;
        break;

      // case hashLocationURL.includes('?isQualId'): // This has to be modified, because the api call is harcoded.
      //   answ.studyMethod = 'BY_QUALS';
      //   answ.qualId = id;
      //   break;
      case hashLocationURL.includes('studyFavoriteQuestion'):
        answ.studyMethod = 'STUDY_FAVORITE_QUESTION';
        answ.rateId = this.props.user.rate.id;
        answ.dontEarnPoints = this.props.resetChapter;
        break;

      case hashLocationURL.includes('studySubmittedQuestions'):
        answ.studyMethod = 'STUDY_SUBMITTED_QUESTION';
        answ.rateId = this.props.user.rate.id;
        answ.dontEarnPoints =
          this.props?.question?.submittedByMe || this.props.resetChapter;
        break;

      case hashLocationURL.includes('studyMyFavoriteQuestion'):
        answ.studyMethod = 'STUDY_MY_FAVORITE_QUESTION';
        answ.rateId = this.props.user.rate.id;
        answ.dontEarnPoints = this.props.resetChapter;
        break;

      case hashLocationURL.includes('studyMySubmittedQuestion'):
        answ.studyMethod = 'STUDY_MY_SUBMITTED_QUESTION';
        answ.rateId = this.props.user.rate.id;
        answ.dontEarnPoints =
          this.props.question?.submittedByMe || this.props.resetChapter;
        break;

      case hashLocationURL.includes('?isPersonalizedStudyAll'):
        answ.studyMethod = 'PERSONALIZED_STUDY_ALL';
        answ.rateId = this.props.user.rate.id;
        break;

      default:
        answ.studyMethod = 'STUDY_ALL';
        answ.rateId = this.props.user.rate.id;
        break;
    }

    this.props.sendAnswer(answ);
    this.setState({ pendingAnswer: answ });
  };

  resetChapter = ({ userId, chapterId, method }) => {
    if (!method && chapterId) {
      this.props.updateUserAnswers({ user: userId, chapter: chapterId });
    }
    if (method && !chapterId) {
      this.props.updateUserAnswers({ user: userId, method });
    }
  };

  handleNextQuestion = (answ) => {
    if (this.props.cantStudy || this.state.showTrialModal) {
      return;
    }

    if (this.props.answerResponse === ANSWERS_LIMIT_REACHED) {
      this.setState({ showTrialModal: true });
      return;
    }

    // If we have a pending answer, don't advance yet
    if (this.state.pendingAnswer) {
      return;
    }

    if (this.props.questionsData[0] !== NO_MORE_QUESTIONS_TEXT) {
      // First of all we set the previous question with the previous counters
      this.props.setPreviousQuestion(this.props.question);
      this.props.setPreviousCounters(this.props.counters);
      // If there are more questions we keep iterating, otherwise is finished
      this.props.setQuestion(
        // This way we go to the next question, poping from the array and asigning to the question field
        this.props.questionsData[0],
        this.props.questionsData,
      );

      this.props.updateCounters(answ);

      this.setState({
        questionIndex: this.state.questionIndex + 1,
        clicked: false,
        showPreviousAnswer: false,
      });
    } else {
      const userId = this.props.user.id;
      const chapterId = this.props.question.chapter;
      let itemIndex = getSessionStorageItem('nextUpItem');
      if (this.props.resetChapter) {
        const hashLocationURL = window.location.hash;
        if (hashLocationURL.includes('studyFavoriteQuestion')) {
          this.resetChapter({ userId, method: 'favorite' });
        } else if (hashLocationURL.includes('studySubmittedQuestions')) {
          this.resetChapter({ userId, method: 'submit' });
        } else if (hashLocationURL.includes('studyMyFavoriteQuestion')) {
          this.resetChapter({ userId, method: 'myFavorite' });
        } else if (hashLocationURL.includes('studyMySubmittedQuestion')) {
          this.resetChapter({ userId, method: 'mySubmit' });
        } else {
          this.resetChapter({ userId, chapterId });
        }
      }
      setSessionStorageItem('nextUpItem', ++itemIndex);
      if (answ.studyMethod === 'STUDY_FAVORITE_QUESTION') {
        this.props.setIsStudyFavoriteQuestion(true);
      }
      if (answ.studyMethod === 'STUDY_SUBMITTED_QUESTION') {
        this.props.setIsStudyingSubmittedQuestions(true);
      }
      this.props.history('/app/flashcard', { replace: true });
    }
  };

  setPreviousAnswerClicked = (answerClicked) => {
    this.setState({
      previousAnswerClicked: answerClicked,
    });
  };

  handlePreviousQuestion = () => {
    this.props.setBackedStateToQuestion(); // The user CAN NOT go back more than one time so we create the BACKED state.
    this.props.previousQuestionToActual();
    this.props.getAllFavoriteQuestions();
    this.setState({ clicked: true, showPreviousAnswer: true });
  };

  getClickedState = () => this.state.clicked;

  setClicked = () => {
    this.setState({
      clicked: true,
    });
  };

  async componentDidMount() {
    this.props.clearAnswerResponse();
    this.props.setCantStudy(false);
    this.setState({
      isMounted: false,
    });
    this.props.cleanQuestions();
    const hashLocationURL = window.location.hash;
    const hashLocation = hashLocationURL.split('/').splice(3, 2);

    if (hashLocation.length === 0) {
      this.props.getTrialUserProgress(null, 'STUDY_ALL');
      return;
    }

    const studyProgramURL = hashLocation.length > 0 ? hashLocation[0] : null;
    let idURL = null;
    let studyMethodURL = null;
    if (hashLocation.length > 0) {
      if (
        hashLocation[0] === 'studyFavoriteQuestion' ||
        hashLocation[0] === 'studySubmittedQuestions' ||
        hashLocation[0] === 'studyMyFavoriteQuestion' ||
        hashLocation[0] === 'studyMySubmittedQuestion'
      ) {
        idURL =
          this.props.user && this.props.user.studyPrograms
            ? getCurrentStudyProgram(this.props.user, this.props.pfeSktId).id
            : 0;

        studyMethodURL = hashLocation[0];
      } else {
        if (hashLocation.length > 1 && hashLocation[1]) {
          const parts = hashLocation[1].split('?');
          idURL = parts[0];
          if (parts.length > 1) {
            studyMethodURL = parts[1].split('&')[0];
          }
        } else {
          // Fallback: Malformed URL - redirect to study home
          this.props.history('/app/study');
          return;
        }
      }
    }

    if (hashLocationURL && !studyProgramURL) {
      alert('An error occurred, you will be redirected');
      this.props.history('/app/study');
    }

    if (this.props.userSubscription?.status === 'trialing') {
      let studyMethod = '';
      switch (studyMethodURL) {
        case 'isChapterId':
          studyMethod = 'BY_REFERENCE';
          break;

        // case 'isQualId': // This has to be modified, because the api call is harcoded.
        //   studyMethod = 'BY_QUALS';
        //   break;

        case 'studyMySubmittedQuestion':
          studyMethod = 'STUDY_MY_SUBMITTED_QUESTION';
          break;
        case 'studySubmittedQuestions':
          studyMethod = 'STUDY_SUBMITTED_QUESTION';
          break;
        case 'studyMyFavoriteQuestion':
          studyMethod = 'STUDY_MY_FAVORITE_QUESTION';
          break;
        case 'studyFavoriteQuestion':
          studyMethod = 'STUDY_FAVORITE_QUESTION';
          break;
        default:
          studyMethod = 'STUDY_ALL';
          break;
      }
      try {
        await this.props.getTrialUserProgress(idURL, studyMethod);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }

      if (this.props.cantStudy) {
        this.setState({ showTrialModal: true });
      }
    }
  }

  // In the next method we are going to keep getting questions when the user is answering, if there're no more questions we go to
  // /app/flashcard.

  reloadPageGetQuestions = (nextProps) => {
    const hashLocationURL = window.location.hash;
    const hashLocation = hashLocationURL.split('/').splice(3, 2);

    const studyProgramURL = hashLocation[0];
    let idURL = null;
    if (hashLocation.length > 0) {
      if (
        hashLocation[0] === 'studyFavoriteQuestion' ||
        hashLocation[0] === 'studySubmittedQuestions' ||
        hashLocation[0] === 'studyMyFavoriteQuestion' ||
        hashLocation[0] === 'studyMySubmittedQuestion'
      ) {
        idURL = hashLocation[0];
      } else {
        if (hashLocation.length > 1 && hashLocation[1]) {
          idURL = hashLocation[1].split('?')[0];
        } else {
          // Malformed URL
          return;
        }
      }
    }
    const studyMethodURL =
      hashLocation.length > 0 &&
      hashLocation[0] !== 'studyFavoriteQuestion' &&
      hashLocation[0] !== 'studySubmittedQuestions' &&
      hashLocation[0] !== 'studyMyFavoriteQuestion' &&
      hashLocation[0] !== 'studyMySubmittedQuestion'
        ? (() => {
            const parts = hashLocation[1].split('?');
            return parts.length > 1 ? parts[1].split('&')[0] : null;
          })()
        : null;

    const location = this.props.location.search;

    switch (true) {
      case this.props.location.pathname.includes('studyFavoriteQuestion'):
        if (!this.props.loadResetChapter) {
          nextProps.getQuestionsByFavoriteQuestions([], true);
        }

        break;

      case this.props.location.pathname.includes('studySubmittedQuestions'):
        if (!this.props.loadResetChapter) {
          nextProps.getQuestionsBySubmittedQuestions([], true);
        }
        break;

      case this.props.location.pathname.includes('studyMyFavoriteQuestion'):
        if (!this.props.loadResetChapter) {
          nextProps.getQuestionsByMyFavoriteQuestions([], true);
        }
        break;

      case this.props.location.pathname.includes('studyMySubmittedQuestion'):
        if (!this.props.loadResetChapter) {
          nextProps.getQuestionByMySubmittedQuestions([], true);
        }
        break;

      case location.includes('isPersonalizedStudyAll'):
        nextProps.getQuestionsByPersonalizedStudyAll(
          nextProps.user.studyPrograms[0],
          [],
          true,
        );
        break;

      case location === '':
        nextProps.getQuestionsByRate([], true);
        break;
      case studyMethodURL === 'isChapterId':
        nextProps.getQuestionsByChapter(
          idURL,
          studyProgramURL,
          [], // In the first iteration, the stacked questions will be an empty array.
          true,
        );
        break;

      case studyMethodURL === 'isStudyPlanChapter':
        nextProps.getQuestionsByChapter(
          idURL,
          studyProgramURL,
          [], // In the first iteration, the stacked questions will be an empty array.
          true,
        );
        break;

      // case studyMethodURL === 'isQualId': // This has to be modified, because the api call is harcoded.
      //   nextProps.getQuestionsByQuals(idURL, studyProgramURL, [], true);
      //   break;

      default:
        nextProps.getQuestionsByRate([], true);
        break;
    }
  };

  render() {
    let breadcrums = [];

    switch (this.props.backTo) {
      case '/app/study':
        breadcrums = [
          { to: '/app/study', text: 'STUDY HOME' },
          { to: '/app/study', text: 'BY REFERENCE' },
        ];
        break;

      // case '/app/study/quals-and-certs':
      //   breadcrums = [
      //     { to: '/app/study', text: 'STUDY HOME' },
      //     { to: '/app/study/quals-and-certs', text: 'QUALS AND CERTS' },
      //   ];
      //   break;

      case '/app/personalized-study':
        breadcrums = [
          { to: '/app/study', text: 'STUDY HOME' },
          { to: '/app/personalized-study', text: 'PERSONALIZED STUDY' },
        ];
        break;

      default:
        breadcrums = [
          { to: '/app/study', text: 'STUDY HOME' },
          { to: '/app/study', text: 'BY REFERENCE' },
        ];
        break;
    }

    // For preview mode, use the preview question data
    const questionToDisplay = this.state.isPreviewMode
      ? this.state.previewQuestion
      : this.props.question;

    const questionsDataToCheck = this.state.isPreviewMode
      ? this.state.questionsData
      : this.props.questionsData;

    return (
      <div
        style={
          this.state.isPreviewMode
            ? { pointerEvents: 'none', userSelect: 'none' }
            : {}
        }
      >
        {questionsDataToCheck.length !== 0 && questionToDisplay && (
          <QuestionMain
            getCountQuestions={this.props.getCountQuestions}
            setCountQuestions={this.props.setCountQuestions}
            addCountQuestions={this.props.addCountQuestions}
            getRecaptchaTime={this.props.getRecaptchaTime}
            setRecaptchaTime={this.props.setRecaptchaTime}
            setResetChapter={this.props.setResetChapter}
            updateIsCorrect={this.updateIsCorrect}
            handleSendAnswer={this.handleSendAnswer}
            breadcrums={breadcrums}
            clicked={this.state.clicked}
            setClicked={this.setClicked}
            getClickedState={this.getClickedState}
            question={questionToDisplay}
            remaining={this.props.counters}
            disablePrevious={
              this.props.previousQuestion.id === undefined ||
              !!this.state.showPreviousAnswer ||
              !!this.props.previousQuestion.backed ||
              this.state.clicked === true
            }
            renderViewPdfButton={
              !!this.state.showPreviousAnswer || this.state.clicked === true
            }
            isCorrect={this.state.isCorrect}
            handlePreviousQuestion={this.handlePreviousQuestion}
            setPreviousAnswerClicked={this.setPreviousAnswerClicked}
            showPreviousAnswer={this.state.showPreviousAnswer}
            previousAnswerClicked={this.state.previousAnswerClicked}
            openPdfViewer={this.props.openPdfViewer}
            user={this.props.user}
            flashcard={this.props.flashcard}
            resetChapter={this.props.resetChapter}
            getPdfByReference={this.props.getPdfByReference}
            userSubscription={this.props.userSubscription}
            answerResponse={this.props.answerResponse}
            clearAnswerResponse={this.props.clearAnswerResponse}
            activateSubscription={() => this.props.activateSubscription()}
            subscriptionActivationSucces={
              this.props.subscriptionActivationSucces
            }
            subscriptionActivationWaiting={
              this.props.subscriptionActivationWaiting
            }
            subscriptionActivationMessage={
              this.props.subscriptionActivationMessage
            }
            cantStudy={this.props.cantStudy}
            location={this.props.location}
            breadcrumsQualsAndCerts={this.props.breadcrumsQualsAndCerts}
            favoriteQuestion={this.props.favoriteQuestion}
            loadFavoriteQuestion={this.props.loadFavoriteQuestion}
            setFavoriteMessage={this.props.setFavoriteMessage}
            setFavoriteMessageAction={this.props.setFavoriteMessageAction}
            listFavoriteQuestions={this.props.listFavoriteQuestions}
            loadingFavoriteQuestion={this.props.loadingFavoriteQuestions}
            isStudyingSubmittedQuestions={
              this.props.isStudyingSubmittedQuestions
            }
            isStudyingFavoriteQuestions={this.props.isStudyingFavoriteQuestions}
            checkChapterInReview={this.props.checkChapterInReview}
            isLoadingQuestions={this.state.loadingQuestion}
            errorMessageGetQuestion={this.props.errorMessageGetQuestion}
            isPreviewMode={this.state.isPreviewMode}
            showTrialModal={this.state.showTrialModal}
            onCloseTrialModal={() => this.setState({ showTrialModal: false })}
          />
        )}
        {questionsDataToCheck.length === 0 && !this.state.isPreviewMode && (
          <div>
            {this.props.errorMessageGetQuestion ? (
              <div className="messageError">
                {this.props.errorMessageGetQuestion}
              </div>
            ) : (
              <CircularLoading className="loading-gif" />
            )}
          </div>
        )}
        {this.state.isPreviewMode &&
          (!questionToDisplay || questionsDataToCheck.length === 0) && (
            <div className="messageError">
              Invalid or missing question preview data
            </div>
          )}
      </div>
    );
  }
}

export default QuestionContainer;
